import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remend from 'remend';
import { CleanContainer, createCleanMarkdownComponents, normalizeMarkdownBulletLists } from '../utils/markdownHelpers';
import { processQuotes } from '../utils/general';
import CodeBlockWithEmail from './CodeBlockWithEmail';
import EmailDraftCard from './EmailDraftCard';
import SourcesPills from '../../SourcesPills';
import { useTokenAnimator } from '../../../hooks/useTokenAnimator';
import '../../../styles/chat.css';
import './GracefulStreamingMarkdown.css';

const GracefulStreamingMarkdown = ({
  children,
  isStreaming,
  darkMode,
  toolIndicators,
  onSendDraft,
  sendDraftLoading = false,
  sentDrafts = [],
  chatTitle,
  onOpenArtifact,
  messageId,
  chatId,
  onDraftSent,
  onSaveDraftContent,
  currentMessageText,
  responseIndex,
  ...props
}) => {
  const [isTyping, setIsTyping] = useState(false);

  // Extract sources from markdown (same as original)
  const extractSourcesFromMarkdown = (text) => {
    const sourcesRegex = /\n\n\*\*Sources:\*\*\n((?:(?:\d+\.\s*\[.+?\]\(.+?\)\n?)+))/;
    const match = text.match(sourcesRegex);
    if (match) {
      const mainContent = text.replace(match[0], '');
      const sourceMatches = match[1].match(/\d+\.\s*\[(.+?)\]\((.+?)\)/g);
      const allSources = sourceMatches ? sourceMatches.map(sourceMatch => {
        const linkMatch = sourceMatch.match(/\[(.+?)\]\((.+?)\)/);
        return linkMatch ? { title: linkMatch[1], url: linkMatch[2] } : null;
      }).filter(Boolean) : [];
      return { mainContent, sources: allSources };
    }
    return { mainContent: text, sources: [] };
  };

  // Smooth bursty network arrivals into a steady reveal while preserving
  // live markdown rendering through remend + react-markdown.
  const animatedText = useTokenAnimator(children, isStreaming);
  const textForRender = isStreaming ? animatedText : children;

  const markdownComponents = useMemo(() => 
    createCleanMarkdownComponents(darkMode, 0, CodeBlockWithEmail, { 
      onSend: onSendDraft, 
      sendDraftLoading, 
      sentDrafts, 
      chatTitle, 
      isStreaming,
      onOpenArtifact,
      messageId,
      chatId,
      onDraftSent,
      onSaveDraftContent,
      currentMessageText,
      responseIndex,
    }), 
    [darkMode, onSendDraft, sendDraftLoading, sentDrafts, chatTitle, isStreaming, onOpenArtifact, messageId, chatId, onDraftSent, onSaveDraftContent, currentMessageText, responseIndex]
  );

  const splitStreamingEmailSegments = useCallback((text) => {
    const segments = [];
    if (!text) return segments;

    const openerRegex = /```(email|gmail|outlook)\s*\n/gi;
    let cursor = 0;
    let match;

    openerRegex.lastIndex = 0;
    while ((match = openerRegex.exec(text)) !== null) {
      const openerStart = match.index;
      const openerEnd = openerRegex.lastIndex;
      const source = (match[1] || 'email').toLowerCase();

      if (openerStart > cursor) {
        segments.push({ type: 'text', content: text.slice(cursor, openerStart) });
      }

      const remaining = text.slice(openerEnd);
      const closingMatch = remaining.match(/\n```/);

      if (closingMatch && typeof closingMatch.index === 'number') {
        const emailContentEnd = openerEnd + closingMatch.index;
        const emailContent = text.slice(openerEnd, emailContentEnd);
        segments.push({ type: 'email', content: emailContent, source, isClosed: true });
        cursor = emailContentEnd + closingMatch[0].length;
        openerRegex.lastIndex = cursor;
      } else {
        const emailContent = text.slice(openerEnd);
        segments.push({ type: 'email', content: emailContent, source, isClosed: false });
        cursor = text.length;
        break;
      }
    }

    if (cursor < text.length) {
      segments.push({ type: 'text', content: text.slice(cursor) });
    }

    return segments;
  }, []);

  const hasStreamingEmailFence = useCallback((text) => /```(?:email|gmail|outlook)\b/i.test(text || ''), []);

  // Stable key mirrors mobile's stableEmailKey.
  // We use messageId + source + segment index so the key is constant from the moment the
  // opener appears — it must NOT change as To:/Subject: stream in, otherwise React remounts
  // and the user sees a flash. messageId is stable for the lifetime of the stream.
  const getStableEmailKey = useCallback((content, source, segmentIndex = 0) => {
    return `email-stable-${messageId || 'msg'}-${source}-${segmentIndex}`;
  }, [messageId]);



  // Handle content changes - set isTyping for streaming state
  useEffect(() => {
    if (!children || typeof children !== 'string') {
      setIsTyping(false);
      return;
    }
    if (isStreaming) {
      setIsTyping(true);
    } else {
      setIsTyping(false);
    }
  }, [children, isStreaming]);

  // Handle case where text is empty or undefined
  if (!children) {
    return toolIndicators ? (
      <div className="mb-2">
        {toolIndicators}
      </div>
    ) : null;
  }

  // Render content - single ReactMarkdown
  const renderContent = () => {
    const streamSafeText = isStreaming
      ? remend(textForRender || '', { linkMode: 'text-only' })
      : (textForRender || '');
    const processedText = normalizeMarkdownBulletLists(processQuotes(streamSafeText));
    const { sources } = extractSourcesFromMarkdown(processedText);

    // Mobile-exact pattern:
    // - Render EmailDraftCard as soon as the email opener is detected (open or closed fence)
    // - Stable key keeps the same React instance as content streams in (card fills live)
    // - Text before/after renders normally
    // - No raw fence chrome ever visible
    if (isStreaming && hasStreamingEmailFence(processedText)) {
      const segments = splitStreamingEmailSegments(processedText);
      const firstSegmentIsEmail = segments[0]?.type === 'email';
      const content = (
        <div className={`streaming-content bot-response${firstSegmentIsEmail ? ' first-content-email' : ''}`}>
          {segments.map((segment, index) => {
            if (segment.type === 'email') {
              const rawContent = segment.content;
              const emailSource = segment.source === 'gmail' || segment.source === 'outlook' ? segment.source : 'gmail';
              const stableKey = getStableEmailKey(rawContent, segment.source, index);
              const prevSegments = segments.slice(0, index);
              const hasTextBefore = prevSegments.some(s => s.type === 'text' && s.content.trim());
              // No onEdit during streaming — wired up after stream via normal CodeBlockWithEmail path
              return (
                <EmailDraftCard
                  key={stableKey}
                  rawContent={rawContent}
                  darkMode={darkMode}
                  emailSource={emailSource}
                  onSend={onSendDraft}
                  sendDraftLoading={sendDraftLoading}
                  sentDrafts={sentDrafts}
                />
              );
            }
            const trimmed = segment.content.trim();
            if (!trimmed) return null;
            return (
              <ReactMarkdown
                key={`md-${index}`}
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
                {...props}
              >
                {trimmed}
              </ReactMarkdown>
            );
          })}
        </div>
      );

      if (toolIndicators) {
        return (
          <>
            <div className="mb-2">
              {toolIndicators}
            </div>
            <CleanContainer>
              {content}
            </CleanContainer>
            {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
          </>
        );
      }

      return (
        <>
          <CleanContainer>
            {content}
          </CleanContainer>
          {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
        </>
      );
    }
    
    // For non-streaming content, render normally
    if (!isStreaming) {
      const { mainContent } = extractSourcesFromMarkdown(processedText);
      const content = (
        <ReactMarkdown
          components={markdownComponents}
          remarkPlugins={[remarkGfm]}
          {...props}
        >
          {mainContent}
        </ReactMarkdown>
      );
      
      if (toolIndicators) {
        return (
          <>
            <div className="mb-2">
              {toolIndicators}
            </div>
            <CleanContainer>
              <div className="streaming-content bot-response">
                {content}
              </div>
            </CleanContainer>
            {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
          </>
        );
      }
      
      return (
        <>
          <CleanContainer>
            <div className="streaming-content bot-response">
              {content}
            </div>
          </CleanContainer>
          {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
        </>
      );
    }
    
    // Streaming (no email): render markdown continuously with remend pre-processing
    // so incomplete fences/emphasis still display cleanly while tokens arrive.
    const { mainContent: streamingMainContent } = extractSourcesFromMarkdown(processedText);
    const content = (
      <ReactMarkdown
        components={markdownComponents}
        remarkPlugins={[remarkGfm]}
        {...props}
      >
        {streamingMainContent}
      </ReactMarkdown>
    );
    
    const wrapperClass = 'streaming-content bot-response';

    if (toolIndicators) {
      return (
        <>
          <div className="mb-2">
            {toolIndicators}
          </div>
          <CleanContainer>
            <div className={wrapperClass}>
              {content}
            </div>
          </CleanContainer>
          {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
        </>
      );
    }
    
    return (
      <>
        <CleanContainer>
          <div className={wrapperClass}>
            {content}
          </div>
        </CleanContainer>
        {sources.length > 0 && !isTyping && <SourcesPills sources={sources} />}
      </>
    );
  };

  return (
    <div className="streaming-markdown">
      {renderContent()}
    </div>
  );
};

export default GracefulStreamingMarkdown; 